/* ---------------------------------------------------------------------------
   The one editing session on the page.

   Exactly one row across all resource-entry grids is open for editing at a time, and
   every way out of it (Enter, Tab past the last editor, a press outside the row) routes
   through closeSession here. That is the whole point of this module: the exit paths used
   to be spread across six flags carried on the grid object, a dataBound handler that
   reopened an editor after every rebind, and one document click handler per widget, and
   they disagreed with each other. A rebind triggered by a save on an unrelated grid could
   move focus, and a press that touched nothing could produce a save.

   Two rules keep it that way:

     - nothing reopens an editor from a grid event. Opening happens only from a press on a
       cell, through openSession.
     - the row is saved on close only when it is actually dirty. On the Schedule 5 page a
       save costs a sync plus four top-grid reads, so a stray press must not trigger one.
   --------------------------------------------------------------------------- */

const EDIT_ROW = 'tr.k-grid-edit-row';

/*
 * A NumericTextBox cell holds two inputs, and they are not interchangeable.
 *
 * FOCUS_TARGET is the one shown while the cell is idle, and the one focus has to be aimed
 * at: Kendo's _focusin then hides it, shows the element beside it and moves focus there.
 * So VALUE_HOLDER is what actually holds focus while a cell is being typed into, and its
 * focusout is what writes the value onto the model.
 *
 * Confusing the two is worth avoiding carefully. Collecting FOCUS_TARGET and then comparing
 * it against the focused element makes Tab close the row on the first keystroke, and
 * blurring FOCUS_TARGET instead of VALUE_HOLDER means a row reports no pending changes and
 * the edit is dropped on close.
 */
const FOCUS_TARGET = 'input.k-formatted-value';
const VALUE_HOLDER = '[data-role]';

// { widget } for the row currently open, or null when nothing is being edited.
let session = null;

// Armed by a press that closed a session, consumed by the selection change that press
// goes on to raise. This is what stops a press on another row from opening that row.
let suppressNextOpen = false;

let documentHandlerAttached = false;

export const getSession = () => session;

/*
 * The open row's editable cells, in column order. Cells rather than inputs, because the
 * element focus was aimed at is not the element that ends up focused, so nothing can be
 * identified reliably by its input. The cell is stable either way.
 *
 * With locked columns the grid renders the edit row twice, once per table, so this
 * deliberately collects across both.
 */
export const editorCellsOf = (grid) =>
    grid.element.find(EDIT_ROW).find('td')
        .filter((_, cell) => $(cell).find(FOCUS_TARGET).length > 0)
        .toArray();

/** The editable cell an element sits in, whichever of the cell's inputs it happens to be. */
export const editorCellOf = (element) => $(element).closest('td')[0] ?? null;

/**
 * Whether a keystroke came from inside an editor rather than from a closed cell.
 *
 * Navigatable puts focus on the cell, not on an input, so this separates "typing in a cell"
 * from "a cell is merely current". Enter needs the distinction: Kendo's navigatable keydown
 * handler is bound on grid.table, inside grid.element where ours is, so Kendo's runs first,
 * and on a closed row it opens the row itself and the edit event adopts a session for it.
 * Acting on any Enter would shut that row again in the same keystroke.
 */
export const isEditorElement = (target) => !!target && $(target).is(`${FOCUS_TARGET}, ${VALUE_HOLDER}`);

/** Put focus in a cell, aimed at the input Kendo expects to receive it. */
export const focusEditorCell = (cell) => {
    const input = $(cell).find(FOCUS_TARGET)[0];
    if (input) input.focus();
};

/**
 * The editor Tab should move to, staying inside the row.
 *
 * Null means there is nowhere left to go, which the caller reads as "close the row".
 * Returning the next row's first editor is what the old handler did, and is what "never
 * switch to other rows" rules out.
 */
export const nextEditableInRow = (inputs, current, shiftKey = false) => {
    const index = inputs.indexOf(current);
    if (index === -1) return null;

    const nextIndex = shiftKey ? index - 1 : index + 1;
    if (nextIndex < 0 || nextIndex >= inputs.length) return null;

    return inputs[nextIndex];
};

/**
 * Whether a pressed column carries an editor. The static columns come first, so anything
 * before the first editable one is Item Name, Projected Cost, Variance or Actual Cost.
 * Pressing one of those is a way of pressing outside the row, not a way of opening it.
 */
export const isEditableCell = (cellIndex, firstEditableIndex) =>
    firstEditableIndex >= 0 && cellIndex >= firstEditableIndex;

/**
 * Which editor a pressed cell corresponds to. The editors in a row are offset from the
 * grid's column indexes by the static columns in front of them.
 */
export const editorIndexForCell = (cellIndex, firstEditableIndex, inputCount) => {
    if (inputCount <= 0) return -1;

    const index = cellIndex - firstEditableIndex;
    if (index < 0) return -1;
    if (index >= inputCount) return inputCount - 1;

    return index;
};

/**
 * Whether a press landed inside one of the open row's editors.
 *
 * Narrower than the edit row, deliberately: with locked columns Kendo renders the open row
 * in both tables, so its static cells are inside tr.k-grid-edit-row too. Treating the whole
 * row as inside meant pressing Actual Cost on the open row did nothing at all.
 */
export const isInsideEditors = (grid, target) => {
    const cell = editorCellOf(target);
    if (!cell) return false;

    return editorCellsOf(grid).includes(cell);
};

/**
 * Whether a press landed on an element's own scrollbar rather than its content.
 *
 * A scrollbar lives inside its element's border box but outside its client box, and a press
 * on one reports the element itself as the target, which is otherwise indistinguishable from
 * a press outside the row. The overflow check matters: without it an ordinary press near the
 * bottom edge of any element would be read as a scrollbar.
 */
export const isScrollbarPress = (element, offsetX, offsetY) => {
    if (!element) return false;

    const onVerticalBar = element.scrollHeight > element.clientHeight && offsetX > element.clientWidth;
    const onHorizontalBar = element.scrollWidth > element.clientWidth && offsetY > element.clientHeight;

    return !!(onVerticalBar || onHorizontalBar);
};

/** Open a row for editing, focused on the cell that was pressed. */
export const openSession = (widget, cell) => {
    const $cell = $(cell);
    const row = $cell.closest('tr');
    if (!row.length) return;

    const { grid } = widget;
    // Pressing a static column is one of the ways of pressing outside the row, so it is not
    // a way of opening one either.
    if (!isEditableCell(grid.cellIndex($cell), grid.firstEditableIndex)) return;

    session = { widget };
    grid.editRow(row);

    const cells = editorCellsOf(grid);
    if (!cells.length) {
        // The grid declined to open the row; beforeEdit refuses rows with no projected
        // hours. A session for a row that never opened could never be closed.
        session = null;
        return;
    }

    const index = editorIndexForCell(grid.cellIndex($cell), grid.firstEditableIndex, cells.length);
    if (index >= 0) focusEditorCell(cells[index]);
};

/**
 * Take ownership of a row the grid opened without going through openSession. Kendo does
 * that on its own: with navigatable: true, Enter on a selected cell reaches _handleEditing,
 * which calls editRow itself. A row open with no session would be unreachable, so the
 * grid's edit event routes through here. Called after openSession on the normal path,
 * where it finds the session already set and does nothing.
 */
export const adoptSession = (widget) => {
    if (session) return;
    if (!editorCellsOf(widget.grid).length) return;

    session = { widget };
};

/*
 * Push the open row's editors to commit. The value the user typed lives inside the
 * NumericTextBox until its focusout runs, so this has to happen before the row is asked
 * whether it has changes; otherwise it answers no and the edit is discarded.
 *
 * Blurring the focused element is what Kendo's own Enter handling does, and is why Enter
 * was the only key that saved. The sweep afterwards covers the case where focus had already
 * moved on before we were called.
 */
const commitOpenEditors = (grid) => {
    const active = document.activeElement;
    if (active && isInsideEditors(grid, active)) active.blur();

    grid.element.find(EDIT_ROW).find(VALUE_HOLDER).trigger('focusout');
};

/** Close the open row, saving it only if it is dirty. */
export const closeSession = () => {
    if (!session) return;

    const { widget } = session;
    // Cleared first: saving rebinds the grid, and nothing may reopen from there.
    session = null;

    const { grid } = widget;
    commitOpenEditors(grid);

    if (grid.dataSource.hasChanges()) grid.saveRow();
    else grid.cancelRow();

    grid.clearSelection();
};

/** Close the open row and discard whatever was typed into it. */
export const cancelSession = () => {
    if (!session) return;

    const { widget } = session;
    session = null;

    widget.grid.cancelRow();
    widget.grid.clearSelection();
};

export const consumeSuppressedOpen = () => {
    const suppressed = suppressNextOpen;
    suppressNextOpen = false;
    return suppressed;
};

/**
 * Attach the page-wide press handler. Safe to call from every widget: only the first call
 * binds. Schedule 5 builds 18 widgets, and binding per widget meant every press on the
 * page ran the close path 18 times.
 *
 * mousedown rather than click, so the session closes before focus moves; that ordering is
 * what made the grid hard to unfocus. Kendo raises the grid's own selection change on
 * mouseup, which is what gives suppressNextOpen something to suppress.
 */
export const attachDocumentCloseHandler = () => {
    if (documentHandlerAttached) return;
    documentHandlerAttached = true;

    $(document).on('mousedown.resourceEntryEditing', (e) => {
        suppressNextOpen = false;
        if (!session) return;
        // Dragging a scrollbar is navigating to the rest of the row, not leaving it.
        if (isScrollbarPress(e.target, e.offsetX, e.offsetY)) return;
        if (isInsideEditors(session.widget.grid, e.target)) return;

        closeSession();
        suppressNextOpen = true;
    });
};

/** Tear the page-wide state down. Used by the tests, and by a page that rebuilds grids. */
export const resetEditing = () => {
    session = null;
    suppressNextOpen = false;
    documentHandlerAttached = false;
    $(document).off('.resourceEntryEditing');
};
